import React from 'react';
import Icon from '../../../components/AppIcon';

const AuditEntriesTable = ({ entries, loading }) => {
  const getSourceIcon = (source) => {
    const icons = {
      system: 'Database',
      compliance_report: 'FileText',
      approval_workflow: 'GitBranch'
    };
    return icons?.[source] || 'Activity';
  };

  const getSourceColor = (source) => {
    const colors = {
      system: 'var(--color-primary)',
      compliance_report: 'var(--color-success)',
      approval_workflow: 'var(--color-warning)'
    };
    return colors?.[source] || 'var(--color-muted-foreground)';
  };

  const getActionColor = (actionType) => {
    if (actionType?.includes('approved') || actionType?.includes('acknowledged')) return 'text-success';
    if (actionType?.includes('submitted') || actionType?.includes('completed')) return 'text-primary';
    if (actionType?.includes('rejected') || actionType?.includes('failed')) return 'text-error';
    return 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date?.toLocaleString('en-PG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4, 5]?.map(i => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left">
                <span className="caption text-xs font-medium text-foreground">Source</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="caption text-xs font-medium text-foreground">Action</span>
              </th>
              <th className="px-4 py-3 text-left hidden md:table-cell">
                <span className="caption text-xs font-medium text-foreground">Entity</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="caption text-xs font-medium text-foreground">Performed By</span>
              </th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">
                <span className="caption text-xs font-medium text-foreground">Timestamp</span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="caption text-xs font-medium text-foreground">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries?.map((entry, index) => (
              <tr key={entry?.id || index} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getSourceIcon(entry?.source)} 
                      size={16} 
                      color={getSourceColor(entry?.source)} 
                    />
                    <span className="caption text-xs text-foreground capitalize hidden sm:inline">
                      {entry?.source?.replace('_', ' ')}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className={`caption text-xs font-medium ${getActionColor(entry?.actionType)}`}>
                    {entry?.actionType}
                  </p>
                  <p className="caption text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {entry?.actionDescription}
                  </p>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <p className="caption text-xs text-foreground">{entry?.entityType}</p>
                  <p className="caption text-xs text-muted-foreground font-mono truncate max-w-[100px]">
                    {entry?.entityId?.substring(0, 8)}...
                  </p>
                </td>
                <td className="px-4 py-3">
                  <p className="caption text-xs text-foreground truncate max-w-[150px]">
                    {entry?.performedBy}
                  </p>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <p className="caption text-xs text-muted-foreground">
                    {formatTimestamp(entry?.performedAt)}
                  </p>
                </td>
                <td className="px-4 py-3 text-center">
                  {entry?.changes && (
                    <button
                      className="text-primary hover:text-primary/80 transition-smooth"
                      onClick={() => {
                        alert(JSON.stringify(entry?.changes, null, 2));
                      }}
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(!entries || entries?.length === 0) && (
        <div className="p-8 text-center">
          <Icon name="History" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="caption text-sm text-muted-foreground">No audit entries found</p>
        </div>
      )}
    </div>
  );
};

export default AuditEntriesTable;